import { Divider, Grid, GridItem, IconButton, ListItem, Text, UnorderedList, VStack } from "@chakra-ui/react";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { EmptyState, For, TooltipTime } from "@equidefi/ui";
import type React from "react";
import { useMemo, useState } from "react";

import { useInvestmentContext } from "@/areas/Investments/InvestmentContext";
import { InvestmentAccreditationSkeleton } from "@/areas/Investments/skeletons/InvestmentAccreditationSkeleton";
import { SectionCard } from "@/components/SectionCard";
import { CanEdit } from "@/components/permissions/Can";
import { USDFormatter } from "@equidefi/i18n";
import { userFullName } from "@equidefi/shared";
import { Icon } from "@equidefi/ui/icon";

const DisplayItem: React.FC<{
  value?: React.ReactNode;
  mask?: boolean;
  label: string;
  gridColumn: number | number[];
  rowSpan?: number | number[];
}> = ({ value, label, mask = false, ...rest }) => {
  const [visible, setVisible] = useState(() => !mask);
  const displayValue = useMemo(() => {
    if (!value) return "N/A";
    if (!visible) return "•••••••••";
    return value;
  }, [value, visible]);

  return (
    <GridItem {...rest}>
      <Text
        mb="1"
        color="secondary.500"
        display="inline-flex"
        justifyContent="center"
        alignItems="center"
        fontWeight="semibold"
        textStyle="context">
        {label}
        {mask && (
          <IconButton
            colorScheme="secondary"
            size="1em"
            ml="2"
            variant="link"
            aria-label={`Toggle ${label} Visibility`}
            aria-pressed={visible ? "true" : "false"}
            onClick={() => setVisible((prev) => !prev)}
            icon={visible ? <Icon.Eye size="1em" /> : <Icon.EyeOff size="1em" />}
          />
        )}
      </Text>
      <Text mb="0">{displayValue}</Text>
    </GridItem>
  );
};

export default function InvestmentAdditionalInfo(): React.JSX.Element {
  const { investment, isLoading, refetch } = useInvestmentContext();
  const { vault } = investment || {};

  useDocumentTitle(["Investment Accreditation", investment?.offering?.name]);

  const isIndividualOrJoint = useMemo(() => {
    return ["INDIVIDUAL", "JOINT"].includes(vault?.account_type || "");
  }, [vault?.account_type]);

  if (isLoading) {
    return <InvestmentAccreditationSkeleton />;
  }

  return (
    <VStack justify="flex-start" align="start" gap={2} flex={2}>
      <SectionCard title="Investor Data" px={[2, 2]} py={[2, 2]} borderRadius="lg" bg="white" boxShadow="sm">
        <Grid templateColumns={["1fr", "1fr", "1fr 1fr"]} gap={2}>
          <DisplayItem label="FULL NAME" value={vault?.first_name && userFullName(vault)} gridColumn={1} />
          <DisplayItem label="PHONE" value={vault?.phone_number} gridColumn={[1, 1, 2]} />
          <DisplayItem label="EMAIL" value={investment?.user?.email ?? "N/A"} gridColumn={1} />
          <DisplayItem
            label="ADDRESS"
            value={
              vault?.address_street_1 && (
                <VStack align="start" gap="0" spacing={0}>
                  <Text mb="0">{vault?.address_street_1}</Text>
                  {vault?.address_street_2 && <Text mb="0">{vault?.address_street_2}</Text>}
                  <Text mb="0">{vault?.address_city}</Text>
                  <Text mb="0">
                    {[vault?.address_state, vault?.address_postal_code]
                      .filter(Boolean)
                      .map((s) => String(s).trim())
                      .join(", ")}
                  </Text>
                  <Text mb="0">{vault?.address_country_code}</Text>
                </VStack>
              )
            }
            gridColumn={[1, 1, 2]}
            rowSpan={[1, 1, 2]}
          />
          <CanEdit userRole="owner" offeringId={investment?.offering_id}>
            <DisplayItem mask label="TAX ID" value={isIndividualOrJoint ? vault?.tax_id : vault?.ein} gridColumn={1} />
            {!isIndividualOrJoint && (
              <DisplayItem mask label="PRIMARY SIGNER TAX ID" value={vault?.tax_id} gridColumn={1} />
            )}
          </CanEdit>
          <DisplayItem
            label="DATE OF BIRTH"
            value={vault?.birthdate && <TooltipTime datetime={vault?.birthdate} format="l" tooltip="ll" />}
            gridColumn={1}
          />

          <GridItem colSpan={[1, 1, 2]}>
            <Divider />
          </GridItem>

          <DisplayItem
            label="SELF-REPORTED ACCREDITATION STATUS"
            value={vault?.accredited ? "Accredited" : vault?.accredited === false ? "Not Accredited" : "N/A"}
            gridColumn={1}
          />
          <DisplayItem
            label={isIndividualOrJoint ? "ANNUAL INCOME" : "REVENUE"}
            value={vault?.annual_income && USDFormatter(Number(vault.annual_income))}
            gridColumn={[1, 1, 2]}
          />
          <DisplayItem
            label={isIndividualOrJoint ? "NET WORTH" : "NET ASSETS"}
            value={vault?.net_worth && USDFormatter(Number(vault?.net_worth))}
            gridColumn={1}
          />
          <DisplayItem
            label="BROKER-DEALER AFFILIATE"
            value={vault?.broker_dealer ? "Yes" : vault?.broker_dealer === false ? "No" : "N/A"}
            gridColumn={[1, 1, 2]}
          />

          {investment?.offering.collect_additional_info && (
            <>
              <GridItem colSpan={[1, 1, 2]}>
                <Divider />
              </GridItem>

              <For
                each={Object.entries(vault?.additional_info ?? {})}
                fallback={
                  <GridItem colSpan={[1, 1, 2]}>
                    <EmptyState w="full" textColor="gray.400" mb="8">
                      This investor has not completed the additional info form yet.
                    </EmptyState>
                  </GridItem>
                }>
                {([key, value], i) => {
                  const gridColumn = i % 2 === 0 ? 1 : [1, 1, 2];

                  return (
                    <DisplayItem
                      key={key}
                      label={value.label.toUpperCase()}
                      gridColumn={gridColumn}
                      value={
                        Array.isArray(value.value) ? (
                          <UnorderedList pl="0" spacing={0} styleType="none" ml="0">
                            {value.value.map((item: string) => (
                              <ListItem key={item}>{item}</ListItem>
                            ))}
                          </UnorderedList>
                        ) : (
                          value.value
                        )
                      }
                    />
                  );
                }}
              </For>
            </>
          )}
        </Grid>
      </SectionCard>
    </VStack>
  );
}
